// Display formatters. Ported verbatim from the approved reference (calc.js `fmt`).
// Shared by the server (rationale text, CSV export) and the client (rendering) so both read identically.

export function fmtMoney(v: number | null | undefined, o?: { compact?: boolean }): string {
  if (v == null || isNaN(v)) return '—';
  const a = Math.abs(v), s = v < 0 ? '−' : '';
  if (o && o.compact) {
    if (a >= 1e6) return s + '$' + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2) + 'M';
    if (a >= 1e3) return s + '$' + (a / 1e3).toFixed(a >= 1e5 ? 0 : 1) + 'K';
  }
  return s + '$' + Math.round(a).toLocaleString('en-US');
}

/** Exact dollars and cents (reconciliation figures). */
export function fmtMoneyCents(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return (v < 0 ? '−' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtSignedMoney(v: number | null | undefined, o?: { compact?: boolean }): string {
  if (v == null) return '—';
  return (v > 0 ? '+' : v < 0 ? '' : '±') + fmtMoney(v, o);
}

export function fmtPct(v: number | null | undefined, d?: number): string {
  if (v == null || isNaN(v)) return '—';
  return (v * 100).toFixed(d == null ? 1 : d) + '%';
}

export function fmtSignedPct(v: number | null | undefined, d?: number): string {
  if (v == null) return '—';
  const s = (v * 100).toFixed(d == null ? 1 : d);
  return (v > 0 ? '+' : v < 0 ? '−' : '±') + s.replace('-', '') + '%';
}

/** Percentage-point change. Always labeled "pts" — never confused with a percent change. */
export function fmtPts(v: number | null | undefined, d?: number): string {
  if (v == null || isNaN(v)) return '—';
  const s = Math.abs(v).toFixed(d == null ? 1 : d);
  return (v > 0.0499 ? '+' : v < -0.0499 ? '−' : '±') + s + ' pts';
}

export function fmtInt(v: number | null | undefined): string {
  return v == null ? '—' : Math.round(v).toLocaleString('en-US');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO date (YYYY-MM-DD) → "Oct 12, 2025". Pure string handling: no time-zone shifts. */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "Oct 12, 2025 – Oct 12, 2026" style range, collapsing the year for calendar years like the reference. */
export function fmtRange(start: string, end: string): string {
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  if (ys === ye && ms === 1 && ds === 1 && me === 12 && de === 31) return `Jan 1 – Dec 31, ${ys}`;
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function slug(s: string): string {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export const F = {
  money: fmtMoney, cents: fmtMoneyCents, smoney: fmtSignedMoney, pct: fmtPct, spct: fmtSignedPct, pts: fmtPts, int: fmtInt, date: fmtDate,
  range: fmtRange, dateTime: fmtDateTime
};
